import React, { useState, useCallback, useEffect, useMemo } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Expand, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ImageWithFallback } from "./ImageWithFallback";
import { MediaTile } from "./MediaTile";

/**
 * Swipeable gallery (mobile) + click-to-lightbox (desktop).
 * `media`: [{ url, type: "image" | "video" }] — preferred.
 * `images`: array of URLs — kept for the property pages.
 */
export const PhotoGallery = ({ media, images = [], alt = "Photo", testid = "photo-gallery" }) => {
  const items = useMemo(() => {
    const raw = media || images.map((u) => ({ url: u, type: "image" }));
    const seen = new Set();
    return raw.filter((m) => {
      const u = (m?.url || "").trim();
      if (!u || seen.has(u)) return false;
      seen.add(u);
      return true;
    });
  }, [media, images]);

  const [emblaRef, embla] = useEmblaCarousel({ loop: items.length > 1 });
  const [selected, setSelected] = useState(0);
  const [open, setOpen] = useState(false);
  const [lightRef, lightEmbla] = useEmblaCarousel({ loop: items.length > 1 });

  const onSelect = useCallback(() => { if (embla) setSelected(embla.selectedScrollSnap()); }, [embla]);
  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
  }, [embla, onSelect]);

  const openAt = (i) => {
    setOpen(true);
    setTimeout(() => lightEmbla && lightEmbla.scrollTo(i, true), 30);
  };

  if (items.length === 0) {
    return <ImageWithFallback src={null} alt={alt} className="aspect-[16/10] rounded-3xl" testid={`${testid}-empty`} />;
  }

  return (
    <div data-testid={testid}>
      <div className="relative rounded-3xl overflow-hidden group">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {items.map((m, i) => (
              <div key={m.url} className="relative flex-[0_0_100%]" data-testid={`${testid}-slide-${i}`}>
                {m.type === "video" ? (
                  <MediaTile item={m} alt={`${alt} video ${i + 1}`} className="aspect-[16/10]" testid={`${testid}-video-${i}`} />
                ) : (
                  <button onClick={() => openAt(i)} className="block w-full cursor-zoom-in" aria-label={`Open photo ${i + 1}`}>
                    <ImageWithFallback src={m.url} alt={`${alt} ${i + 1}`} className="aspect-[16/10]" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <button onClick={() => openAt(selected)} className="absolute top-3 right-3 rounded-full bg-white/90 backdrop-blur px-3 py-2 text-xs font-medium text-ink shadow-sm inline-flex items-center gap-1.5" data-testid={`${testid}-expand`}>
          <Expand className="w-3.5 h-3.5" /> View all
        </button>

        {items.length > 1 && (
          <>
            <button onClick={() => embla && embla.scrollPrev()} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => embla && embla.scrollNext()} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {items.map((m, i) => (
                <span key={m.url} className={`h-1.5 rounded-full transition-all ${i === selected ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* thumbnail strip (desktop) */}
      {items.length > 1 && (
        <div className="mt-3 hidden md:flex gap-2 overflow-x-auto no-scrollbar">
          {items.map((m, i) => (
            <button key={m.url} onClick={() => embla && embla.scrollTo(i)} className={`shrink-0 rounded-xl overflow-hidden border-2 transition-colors ${i === selected ? "border-brand" : "border-transparent"}`}>
              {m.type === "video"
                ? <span className="w-24 h-16 flex items-center justify-center bg-[#E6E3DD] text-[10px] font-semibold text-ink-soft">Video</span>
                : <ImageWithFallback src={m.url} alt={`thumb ${i + 1}`} className="w-24 h-16" />}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-black border-0 overflow-hidden" data-testid={`${testid}-lightbox`}>
          <div className="overflow-hidden" ref={lightRef}>
            <div className="flex">
              {items.map((m, i) => (
                <div key={m.url} className="flex-[0_0_100%] flex items-center justify-center">
                  {m.type === "video"
                    ? <MediaTile item={m} alt={`${alt} video ${i + 1}`} className="max-h-[85vh] aspect-video" testid={`${testid}-lightbox-video-${i}`} />
                    : <img src={m.url} alt={`${alt} full ${i + 1}`} className="max-h-[85vh] w-auto object-contain" />}
                </div>
              ))}
            </div>
          </div>
          {items.length > 1 && (
            <>
              <button onClick={() => lightEmbla && lightEmbla.scrollPrev()} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/25 text-white p-2.5"><ChevronLeft className="w-6 h-6" /></button>
              <button onClick={() => lightEmbla && lightEmbla.scrollNext()} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/25 text-white p-2.5"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
          <button onClick={() => setOpen(false)} className="absolute top-4 right-4 rounded-full bg-white/15 hover:bg-white/25 text-white p-2"><X className="w-5 h-5" /></button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoGallery;
